"use client";

import { MailCheck } from "lucide-react";
import Link from "next/link";
import { type FormEvent, type ReactNode, useEffect, useState } from "react";
import { toast } from "sonner";

import { AuthHeroPanel } from "@/components/auth/audience-hero";
import { LastUsedPill } from "@/components/auth/last-used-pill";
import { TeracWordmark } from "@/components/icons/TeracWordmark";
import type { Audience } from "@/lib/audience-preference";
import { authClient, signIn } from "@/lib/auth-client";
import { MAGIC_LINK_EXPIRES_IN_LABEL } from "@/lib/magic-link";
import {
  clearPreviewUrlCookies,
  isPreviewDeployment,
  savePreviewUrlToCookie,
} from "@/lib/preview-oauth";
import { safeInternalPath } from "@/lib/safe-redirect";
import { setTermsAcceptanceIntent } from "@/lib/terms-intent";

export type AuthProviderId =
  | "linkedin"
  | "google"
  | "facebook"
  | "microsoft"
  | "github";

export interface AuthProvider {
  id: AuthProviderId;
  /** Provider name; rendered as "Continue with {label}" on the primary button. */
  label: string;
  /** Pre-sized mark for the provider icon row. */
  icon: ReactNode;
  /**
   * Mark for the full-width primary button, when `icon` would not read on its
   * blue fill. The same provider drops into the icon row whenever `magicLink` or
   * `password` is set, so `icon` has to work on the page ground.
   */
  buttonIcon?: ReactNode;
}

/**
 * Everything that differs between audiences (experts vs researchers) and modes
 * (login vs join). The visual shell, OAuth plumbing, and last-used-method
 * tracking are shared; only this config changes.
 */
export interface OAuthSignInConfig {
  /**
   * Small uppercase eyebrow above the heading; omit to hide. Unused by the two
   * audience sign-ins, whose `heading` already names the audience.
   */
  eyebrow?: string;
  /**
   * Which side the hero panel is. Selects its dot field and the marks that pop
   * up on it. Defaults to the expert side, whose blue panel is also the
   * fallback background.
   */
  heroAudience?: Audience;
  heroHeadline: ReactNode;
  heroSubtitle: string;
  /**
   * Hero panel background. The two audiences are deliberately different
   * colours, so the panel reinforces which side you are signing in to; both
   * are dark surfaces, so the panel ink is always white. See AUDIENCE_HERO.
   * Defaults to the brand blue.
   */
  heroPanelClass?: string;
  /**
   * Main heading. Name the audience and the action here ("Sign in as an
   * Expert"), not the product: experts and researchers have their own sign-in
   * pages and a product-level greeting let them sign in on the wrong one.
   */
  heading: string;
  /**
   * Optional line under the heading. Both sign-ins dropped theirs: the heading
   * already names the audience and the buttons say what they do, so the line
   * only restated the screen.
   */
  subheading?: string;
  /**
   * Cross-link to the other audience's sign-in: pinned top-left below `lg`, in
   * the form flow from `lg` up. It replaces the "Change role" control now that
   * there is no gate to go back to, and is how the two routes reach each other.
   *
   * Rendered as production has it: pinned to the panel's top-right corner, a
   * muted prompt, then the label in the body ink, underlined, with the arrow
   * inside the underline.
   */
  crossLink?: { href: string; prompt: string; label: string };
  /** Slot above the heading for alerts (e.g. banned / wrong-email notices). */
  topSlot?: ReactNode;
  /** Primary, full-width provider button. */
  primary: AuthProvider;
  /** Secondary providers rendered as an icon row; omit/empty to hide. */
  secondaries?: AuthProvider[];
  /**
   * When set, renders a passwordless email (magic link) form as the primary
   * action, with the OAuth providers as an icon row below.
   */
  magicLink?: { initialEmail?: string };
  /**
   * When set, renders an email + password form as the primary action, with the
   * OAuth providers as an icon row below. Set only by `/mcp/login?demo=1`, so
   * the OAuth demo/reviewer account can sign in with credentials; every other
   * audience, `/mcp/login` included, uses OAuth or magic link. Takes precedence
   * over `magicLink` if both are set, and demotes the providers, so leave it
   * unset wherever a real user needs to find them.
   */
  password?: boolean;
  /** Where to send the user after a successful sign-in. */
  finalRedirect: string;
  /** Audience/mode-specific side effects (signup-source, intent, etc.). */
  onBeforeSignIn: () => Promise<void> | void;
}

export function OAuthSignIn({ config }: { config: OAuthSignInConfig }) {
  const [loadingProvider, setLoadingProvider] = useState<AuthProviderId | null>(
    null,
  );
  const [lastMethod, setLastMethod] = useState<string | null>(null);
  const [email, setEmail] = useState(config.magicLink?.initialEmail ?? "");
  const [password, setPassword] = useState("");
  const [passwordState, setPasswordState] = useState<"idle" | "submitting">(
    "idle",
  );
  const [magicLinkState, setMagicLinkState] = useState<
    "idle" | "sending" | "sent"
  >("idle");

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (isPreviewDeployment()) {
      savePreviewUrlToCookie();
    } else {
      clearPreviewUrlCookies();
    }
  }, []);

  useEffect(() => {
    setLastMethod(authClient.getLastUsedLoginMethod());
  }, []);

  const handleAuth = async (provider: AuthProviderId) => {
    setLoadingProvider(provider);
    try {
      await config.onBeforeSignIn();
      setTermsAcceptanceIntent(true);

      sessionStorage.setItem("postLoginCallback", config.finalRedirect);

      await signIn.social({
        provider,
        callbackURL: config.finalRedirect,
      });
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : undefined;
      const label = provider.charAt(0).toUpperCase() + provider.slice(1);
      toast.error(`${label} Sign-in Failed`, {
        description:
          errorMessage ||
          `Unable to sign in with ${label}. Please try again or use a different sign-in method.`,
      });
      setLoadingProvider(null);
    }
  };

  const handleMagicLink = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail || magicLinkState === "sending") {
      return;
    }
    setMagicLinkState("sending");
    try {
      await config.onBeforeSignIn();
      setTermsAcceptanceIntent(true);

      const { error } = await signIn.magicLink({
        email: trimmedEmail,
        callbackURL: config.finalRedirect,
        newUserCallbackURL: config.finalRedirect,
      });

      if (error) {
        throw new Error(error.message || "Unable to send sign-in link.");
      }

      setMagicLinkState("sent");
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : undefined;
      toast.error("Couldn't send sign-in link", {
        description:
          errorMessage ||
          "Please try again or continue with one of the providers above.",
      });
      setMagicLinkState("idle");
    }
  };

  const handlePassword = async (e: FormEvent) => {
    e.preventDefault();
    const trimmedEmail = email.trim();
    if (!trimmedEmail || !password || passwordState === "submitting") {
      return;
    }
    setPasswordState("submitting");
    try {
      await config.onBeforeSignIn();
      setTermsAcceptanceIntent(true);

      sessionStorage.setItem("postLoginCallback", config.finalRedirect);

      const { error } = await signIn.email({
        email: trimmedEmail,
        password,
        callbackURL: config.finalRedirect,
      });

      if (error) {
        throw new Error(error.message || "Invalid email or password.");
      }

      // signIn.email returns without navigating; drive the redirect ourselves so
      // an OAuth authorize flow (finalRedirect = the signed continue URL) resumes.
      // Guard against open redirects: finalRedirect can trace back to a
      // user-controlled `redirectTo` query param, and this bypasses Better Auth's
      // trustedOrigins check that the social/magic-link callbackURL paths get.
      window.location.href = safeInternalPath(config.finalRedirect);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : undefined;
      toast.error("Sign-in failed", {
        description:
          errorMessage ||
          "Please check your credentials or continue with a provider below.",
      });
      setPasswordState("idle");
    }
  };

  const secondaries = config.secondaries ?? [];
  const isBusy =
    loadingProvider !== null ||
    magicLinkState === "sending" ||
    passwordState === "submitting";

  const renderProviderIcon = (p: AuthProvider) => {
    // With the tile gone there is nothing to tint, so the pill above the mark
    // is the whole of the last-used signal.
    const isLastUsed = lastMethod === p.id;
    return (
      <div key={p.id} className="relative">
        {isLastUsed && (
          <span className="absolute top-[-14px] left-1/2 z-[2] -translate-x-1/2 whitespace-nowrap">
            <LastUsedPill />
          </span>
        )}
        <button
          type="button"
          onClick={() => handleAuth(p.id)}
          disabled={loadingProvider !== null}
          aria-label={`Continue with ${p.label}`}
          title={`Continue with ${p.label}`}
          // The mark alone, with no tile behind it: four bordered white boxes
          // in a row read as a second set of buttons competing with the primary
          // one above, where the logos on their own read as a quiet footnote.
          // The 52x56 box stays as the hit target.
          //
          // Hover lifts and brightens rather than shading a fill, since there
          // is no fill left to shade. `translate`/`scale` are named in the
          // transition rather than `transform`: Tailwind v4 emits the
          // individual properties, so a list naming only `transform` leaves the
          // lift untransitioned and it snaps.
          className="inline-flex h-[52px] w-[56px] cursor-pointer items-center justify-center rounded-[11px] bg-transparent transition-[opacity,translate,scale] duration-200 ease-out hover:-translate-y-[2px] hover:opacity-75 active:translate-y-0 active:scale-[0.94] active:duration-100 disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:opacity-60 motion-reduce:transition-none motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100"
        >
          {p.icon}
        </button>
      </div>
    );
  };

  const termsNotice = (
    <p className="mt-3 text-center text-[11.5px] leading-[1.55] text-neutral-500 lg:text-[12px] dark:text-neutral-400">
      By continuing, you agree to our{" "}
      <Link
        href="/legal/terms-of-service"
        target="_blank"
        className="hover:text-foreground text-[#606060] underline dark:text-neutral-400"
      >
        Terms
      </Link>{" "}
      &{" "}
      <Link
        href="/legal/privacy-policy"
        target="_blank"
        className="hover:text-foreground text-[#606060] underline dark:text-neutral-400"
      >
        Privacy
      </Link>
      .
    </p>
  );

  const renderProviderRow = (label: string, providers: AuthProvider[]) => (
    <>
      <div className="mt-6 mb-5 flex items-center gap-3">
        <div className="bg-border h-px flex-1" />
        <span className="font-mono text-[10.5px] tracking-[0.08em] text-neutral-500 lowercase lg:text-[11px] dark:text-neutral-400">
          {label}
        </span>
        <div className="bg-border h-px flex-1" />
      </div>
      <div className="flex items-center justify-center gap-[11px] lg:gap-[14px]">
        {providers.map(renderProviderIcon)}
      </div>
    </>
  );

  // The form column is capped at 440px at every width so the subheading holds one
  // line wherever the viewport allows: the longest of the two is the researcher's
  // "Welcome back. Use your work email or a connected account.", which measures
  // 421px at 15px and 392px at the 14px mobile size, and so wrapped in both the
  // 420px column the design specified and the 360px cap this used to carry below
  // `lg`. `w-full` keeps it inside the padding box, which is what binds on a
  // phone (342px at 390px wide), so the copy still wraps there.
  //
  // Note on the responsive ramps below: they are all arbitrary `min-[...]`
  // variants rather than a mix with `lg:`/`xl:`. Tailwind buckets breakpoints by
  // unit before sorting, so a px-bucket `min-[1440px]` rule is emitted *before*
  // a rem-bucket `lg` (64rem) rule; above 1440px both media queries match and
  // the later `lg` one wins, which silently made the 420px column and the 34px
  // heading dead CSS. Keep each ramp in one unit family.
  return (
    // Off-white on both: the provider marks and the email field are unfilled
    // now, so they take this ground rather than sitting on white of their own.
    <div className="bg-marketing-cream text-foreground dark:bg-background grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <AuthHeroPanel
        audience={config.heroAudience ?? "expert"}
        headline={config.heroHeadline}
        subtitle={config.heroSubtitle}
        panelClass={config.heroPanelClass ?? "bg-marketing-blue"}
      />

      {/* Padding is symmetric so it does not disturb the centring, and on the
          breakpoints that render the wordmark it is deep enough to reserve that
          row: paired with the stack's `m-auto`, a form too tall to centre (the
          banned/expert-email variants on a short viewport) stops at the reserved
          band and scrolls instead of sliding under the header. */}
      <div className="relative flex flex-col px-6 py-[76px] sm:px-10 lg:py-12">
        {/* Anchored above the form rather than sitting inside it: in the stack,
            this row's height plus its margin pushed the form below the viewport
            centre and left its contents floating in the gap above. */}
        {/* The wordmark below `lg`, where the hero panel is hidden and the
            form column stands alone. */}
        <Link
          href="/"
          aria-label="Terac home"
          className="absolute top-6 left-6 sm:top-8 sm:left-10 lg:hidden"
        >
          <TeracWordmark className="text-foreground h-9 w-auto" />
        </Link>

        {/* Pinned to the panel's top-right corner at every width, as the
            shipped control is: it belongs to the page rather than the form, and
            in the form flow it pushed the heading down. */}
        {config.crossLink && (
          <Link
            href={config.crossLink.href}
            className="absolute top-6 right-6 inline-flex items-center gap-2 text-[13px] sm:top-8 sm:right-10"
          >
            {/* The prompt is dropped on the narrowest screens: at 390 the full
                row overlapped the wordmark by 63px, and the label alone still
                carries the offer. */}
            <span className="hidden text-[#606060] sm:inline dark:text-neutral-400">
              {config.crossLink.prompt}
            </span>
            <span className="text-foreground underline underline-offset-[3px]">
              {config.crossLink.label} →
            </span>
          </Link>
        )}

        <div className="m-auto flex w-full max-w-[440px] flex-col">
          {config.topSlot}

          {config.eyebrow && (
            <div className="text-marketing-blue dark:text-marketing-blue-light mb-3.5 inline-flex items-center gap-[7px] self-center font-mono text-[10.5px] tracking-[0.16em] uppercase">
              <span className="bg-marketing-blue inline-block h-[5px] w-[5px] rounded-full" />
              {config.eyebrow}
            </div>
          )}
          <h1 className="text-foreground m-0 text-center text-[27px] leading-[1.15] font-medium tracking-[-0.02em] min-[1024px]:text-[30px] min-[1440px]:text-[34px]">
            {config.heading}
          </h1>
          {config.subheading && (
            <p className="mt-2 text-center text-[14px] text-[#606060] lg:text-[15px] dark:text-neutral-400">
              {config.subheading}
            </p>
          )}

          {config.password ? (
            <>
              <form
                onSubmit={handlePassword}
                className="mt-[22px] flex flex-col gap-2.5"
              >
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                  }}
                  placeholder="you@company.com"
                  autoComplete="email"
                  disabled={isBusy}
                  // Unfilled, so it takes the page ground rather than sitting on white of
                  // its own. That leaves the border as the only thing drawing the
                  // field, and `border-border` resolves to #171717 in dark mode,
                  // which is 1.1:1 against the #0a0a0a page and effectively
                  // invisible once the fill is gone. #3a3a44 is the dark edge this
                  // file already uses for interactive surfaces.
                  className="border-border text-foreground focus:border-marketing-blue dark:focus:border-marketing-blue-light h-[52px] w-full rounded-full border bg-transparent px-4 text-[14.5px] outline-none placeholder:text-neutral-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-[#3a3a44] dark:placeholder:text-neutral-400"
                />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  placeholder="Password"
                  autoComplete="current-password"
                  disabled={isBusy}
                  // Unfilled, so it takes the page ground rather than sitting on white of
                  // its own. That leaves the border as the only thing drawing the
                  // field, and `border-border` resolves to #171717 in dark mode,
                  // which is 1.1:1 against the #0a0a0a page and effectively
                  // invisible once the fill is gone. #3a3a44 is the dark edge this
                  // file already uses for interactive surfaces.
                  className="border-border text-foreground focus:border-marketing-blue dark:focus:border-marketing-blue-light h-[52px] w-full rounded-full border bg-transparent px-4 text-[14.5px] outline-none placeholder:text-neutral-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-[#3a3a44] dark:placeholder:text-neutral-400"
                />
                <button
                  type="submit"
                  disabled={isBusy}
                  className="bg-marketing-blue hover:bg-marketing-blue-dark border-marketing-blue dark:border-marketing-blue-light inline-flex h-[52px] w-full cursor-pointer items-center justify-center rounded-full border px-4 text-[14.5px] font-medium tracking-[-0.01em] text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {passwordState === "submitting" ? "Signing in…" : "Sign in"}
                </button>
              </form>

              {termsNotice}
              {renderProviderRow("or with", [config.primary, ...secondaries])}
            </>
          ) : config.magicLink ? (
            magicLinkState === "sent" ? (
              <div className="mt-[22px]">
                <div className="border-marketing-blue/20 dark:border-marketing-blue-light/20 bg-marketing-blue/[0.05] flex items-start gap-3 rounded-[14px] border px-4 py-4">
                  <span className="bg-marketing-blue/10 mt-px flex h-7 w-7 shrink-0 items-center justify-center rounded-full">
                    <MailCheck className="text-marketing-blue dark:text-marketing-blue-light h-[15px] w-[15px]" />
                  </span>
                  <div className="flex-1">
                    <p className="text-foreground m-0 text-[14px] font-medium">
                      Check your inbox
                    </p>
                    <p className="mt-1 mb-0 text-[13px] leading-[1.5] text-[#606060] dark:text-neutral-400">
                      We sent a magic link to{" "}
                      <span className="text-foreground font-medium">
                        {email.trim()}
                      </span>
                      . It expires in {MAGIC_LINK_EXPIRES_IN_LABEL}.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setMagicLinkState("idle");
                  }}
                  className="text-marketing-blue dark:text-marketing-blue-light mt-3 cursor-pointer text-[13px] font-medium tracking-[-0.01em] hover:underline"
                >
                  Use a different email
                </button>
              </div>
            ) : (
              <>
                {/* A wider gap than the password form's 2.5. There, two
                    fields are one pair and belong tight together; here the
                    button is the action, not a second field, and at 10px two
                    identical 52px pills read as one control. */}
                <form
                  onSubmit={handleMagicLink}
                  className="mt-[22px] flex flex-col gap-4"
                >
                  <input
                    type="email"
                    required
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                    }}
                    placeholder="you@company.com"
                    autoComplete="email"
                    disabled={isBusy}
                    // Unfilled, so it takes the page ground rather than sitting on white of
                    // its own. That leaves the border as the only thing drawing the
                    // field, and `border-border` resolves to #171717 in dark mode,
                    // which is 1.1:1 against the #0a0a0a page and effectively
                    // invisible once the fill is gone. #3a3a44 is the dark edge this
                    // file already uses for interactive surfaces.
                    className="border-border text-foreground focus:border-marketing-blue dark:focus:border-marketing-blue-light h-[52px] w-full rounded-full border bg-transparent px-4 text-[14.5px] outline-none placeholder:text-neutral-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-[#3a3a44] dark:placeholder:text-neutral-400"
                  />
                  <button
                    type="submit"
                    disabled={isBusy}
                    className="bg-marketing-blue hover:bg-marketing-blue-dark border-marketing-blue dark:border-marketing-blue-light inline-flex h-[52px] w-full cursor-pointer items-center justify-center rounded-full border px-4 text-[14.5px] font-medium tracking-[-0.01em] text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {magicLinkState === "sending"
                      ? "Sending…"
                      : "Send magic link"}
                  </button>
                </form>

                {termsNotice}
                {renderProviderRow("or with", [config.primary, ...secondaries])}
              </>
            )
          ) : (
            <>
              <div className="relative mt-[22px]">
                {lastMethod === config.primary.id && (
                  <span className="absolute top-[-10px] right-[14px] z-[2]">
                    <LastUsedPill />
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => handleAuth(config.primary.id)}
                  disabled={loadingProvider !== null}
                  className="bg-marketing-blue hover:bg-marketing-blue-dark border-marketing-blue dark:border-marketing-blue-light inline-flex h-[52px] w-full cursor-pointer items-center justify-center gap-2.5 rounded-full border px-4 text-[14.5px] font-medium tracking-[-0.01em] text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {config.primary.buttonIcon ?? config.primary.icon}
                  {loadingProvider === config.primary.id
                    ? "Redirecting…"
                    : `Continue with ${config.primary.label}`}
                </button>
              </div>

              {termsNotice}

              {secondaries.length > 0 &&
                renderProviderRow("or continue with", secondaries)}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
